import React, { useState, useEffect, useContext } from 'react';
import CommentItem from './CommentItem';
import CommentInput from './CommentInput';
import CommentsApi from '../../utils/CommentsApi';
import { AppContext } from '../../AppContext';

export default function CommentList({ transactionId, transactionType = 'CREDITCARD', transactionDescription, transactionAmount, compact = false, showHeader = true, onCommentsChange }) {
    const [comments, setComments] = useState([]);
    const [isExpanded, setIsExpanded] = useState(false);
    const [loading, setLoading] = useState(false);
    const [loaded, setLoaded] = useState(false);
    const { user } = useContext(AppContext);
    
    // Only fetch when modal is opened (component mounts)
    useEffect(() => {
        if (!loaded) {
            loadComments();
        }
    }, []);
    
    const loadComments = async () => {
        setLoading(true);
        try {
            const fetchedComments = await CommentsApi.getComments(transactionId, transactionType);
            setComments(fetchedComments);
            setLoaded(true);
            if (onCommentsChange) {
                onCommentsChange(fetchedComments.length);
            }
        } catch (error) {
            console.error('Error loading comments:', error);
        } finally {
            setLoading(false);
        }
    };
    
    const handleAddComment = async (text) => {
        try {
            const newComment = await CommentsApi.addComment(transactionId, transactionType, text, transactionDescription, transactionAmount);
            const updatedComments = [...comments, newComment];
            setComments(updatedComments);
            if (onCommentsChange) {
                onCommentsChange(updatedComments.length);
            }
        } catch (error) {
            console.error('Error adding comment:', error);
        }
    };
    
    const handleDeleteComment = async (commentId) => {
        try {
            await CommentsApi.deleteComment(commentId);
            const updatedComments = comments.filter(c => c.id !== commentId);
            setComments(updatedComments);
            if (onCommentsChange) {
                onCommentsChange(updatedComments.length);
            }
        } catch (error) {
            console.error('Error deleting comment:', error);
            throw error;
        }
    };
    
    const commentCount = comments.length;
    
    if (compact && !isExpanded) {
        // Compact view - just show count and expand button
        return (
            <div
                onClick={() => setIsExpanded(true)}
                style={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: '6px',
                    padding: '8px 12px',
                    backgroundColor: commentCount > 0 ? '#F0F8FF' : '#F5F5F5',
                    borderRadius: '6px',
                    cursor: 'pointer',
                    direction: 'rtl',
                    marginTop: '8px',
                    border: commentCount > 0 ? '1px solid #B0D4F1' : '1px solid #E0E0E0',
                    transition: 'all 0.2s'
                }}
                onMouseEnter={(e) => e.currentTarget.style.backgroundColor = commentCount > 0 ? '#E3F2FD' : '#EEEEEE'}
                onMouseLeave={(e) => e.currentTarget.style.backgroundColor = commentCount > 0 ? '#F0F8FF' : '#F5F5F5'}
            >
                <span style={{ fontSize: '16px' }}>💬</span>
                <span style={{ fontSize: '14px', fontWeight: '500', color: '#333' }}>
                    {commentCount > 0 ? `${commentCount} הערות` : 'הוסף הערה'}
                </span>
                <span style={{ fontSize: '12px', marginRight: 'auto', color: '#666' }}>▼</span>
            </div>
        );
    }
    
    // Expanded view - show all comments
    return (
        <div style={{
            marginTop: showHeader ? '12px' : '0',
            padding: showHeader ? '12px' : '0',
            backgroundColor: showHeader ? '#FAFAFA' : 'transparent',
            borderRadius: showHeader ? '8px' : '0',
            border: showHeader ? '1px solid #E0E0E0' : 'none',
            direction: 'rtl'
        }}>
            {showHeader && (
                <div style={{
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'space-between',
                    marginBottom: '12px'
                }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                        <span style={{ fontSize: '16px' }}>💬</span>
                        <span style={{ fontSize: '15px', fontWeight: '600', color: '#333' }}>
                            הערות {commentCount > 0 && `(${commentCount})`}
                        </span>
                    </div>
                    {compact && (
                        <button
                            onClick={() => setIsExpanded(false)}
                            style={{
                                background: 'none',
                                border: 'none',
                                fontSize: '12px',
                                color: '#666',
                                cursor: 'pointer',
                                padding: '4px 8px'
                            }}
                        >
                            ▲ סגור
                        </button>
                    )}
                </div>
            )}
            
            {loading ? (
                <div style={{
                    textAlign: 'center',
                    padding: '20px',
                    color: '#999',
                    fontSize: '14px'
                }}>
                    טוען הערות...
                </div>
            ) : commentCount === 0 ? (
                <div style={{
                    textAlign: 'center',
                    padding: '20px',
                    color: '#999',
                    fontSize: '14px'
                }}>
                    אין הערות עדיין. היה הראשון להגיב!
                </div>
            ) : (
                <div style={{
                    maxHeight: showHeader ? '300px' : 'none',
                    overflowY: showHeader ? 'auto' : 'visible',
                    marginBottom: '12px'
                }}>
                    {comments.map(comment => (
                        <CommentItem
                            key={comment.id}
                            comment={comment}
                            onDelete={handleDeleteComment}
                            currentUserId={user?.id}
                        />
                    ))}
                </div>
            )}
            
            <CommentInput onAddComment={handleAddComment} />
        </div>
    );
}